import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '@/lib/api';
import { SwipeRow } from '@/components/SwipeRow';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Eye, Trash2, Users, Upload, ArrowUpDown, SlidersHorizontal, Phone, Mail, AlertTriangle, MessageSquare, Clock } from 'lucide-react';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/AddressAutocomplete';

const stages = ['All', 'New', 'Offer Sent', 'Follow-Up', 'Price Watch', 'Rejected Countered', 'Rejected NC', 'Accepted Other Offer', 'Under Contract', 'Marketed', 'Sold', 'Closed', 'Dead', 'Cancelled'];
const editableStages = ['New', 'Offer Sent', 'Follow-Up', 'Price Watch', 'Rejected Countered', 'Rejected NC', 'Accepted Other Offer', 'Under Contract', 'Marketed', 'Sold', 'Closed', 'Dead', 'Cancelled'];
const propertyTypes = ['All', 'Single Family', 'Multi Family', 'Condo', 'Townhouse', 'Land', 'Commercial'];

const stageBadgeClass = {
  New: 'bg-blue-100 text-blue-700 border-blue-200',
  'Offer Sent': 'bg-purple-100 text-purple-700 border-purple-200',
  'Follow-Up': 'bg-amber-100 text-amber-700 border-amber-200',
  'Price Watch': 'bg-pink-100 text-pink-700 border-pink-200',
  'Rejected Countered': 'bg-orange-100 text-orange-700 border-orange-200',
  'Rejected NC': 'bg-rose-100 text-rose-700 border-rose-200',
  'Accepted Other Offer': 'bg-slate-100 text-slate-700 border-slate-200',
  'Under Contract': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  Marketed: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  Sold: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Closed: 'bg-green-100 text-green-700 border-green-200',
  Dead: 'bg-red-100 text-red-700 border-red-200',
  Cancelled: 'bg-gray-200 text-gray-600 border-gray-300',
};

const stageDot = {
  New: 'bg-blue-500',
  'Offer Sent': 'bg-purple-500',
  'Follow-Up': 'bg-amber-500',
  'Price Watch': 'bg-pink-500',
  'Rejected Countered': 'bg-orange-500',
  'Rejected NC': 'bg-rose-500',
  'Accepted Other Offer': 'bg-slate-500',
  'Under Contract': 'bg-indigo-500',
  Marketed: 'bg-cyan-500',
  Sold: 'bg-emerald-500',
  Closed: 'bg-green-600',
  Dead: 'bg-red-500',
  Cancelled: 'bg-gray-400',
};

const sortOptions = [
  { value: 'created_at:desc', label: 'Newest First' },
  { value: 'created_at:asc', label: 'Oldest First' },
  { value: 'offer_amount:desc', label: 'Highest Offer' },
  { value: 'offer_amount:asc', label: 'Lowest Offer' },
  { value: 'name:asc', label: 'Name A-Z' },
  { value: 'name:desc', label: 'Name Z-A' },
  { value: 'dom:desc', label: 'Most Days on Market' },
];
const domOptions = [['0', 'Any DOM'], ['30', 'DOM 30+ days'], ['60', 'DOM 60+ days'], ['90', 'DOM 90+ days'], ['120', 'DOM 120+ days']];

const inputClass = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#0073ea]/50 focus:ring-[#0073ea]";

const defaultForm = {
  name: '', property_address: '', property_city: '', property_state: '', property_zip: '',
  property_type: '', asking_price: '', offer_amount: '', agent_name: '', agent_email: '',
  agent_phone: '', agent_company: '', notes: '',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}

function formatCurrency(val) {
  if (!val || val <= 0) return null;
  return '$' + Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function buildFullAddress(lead) {
  const parts = [lead.property_address];
  if (lead.property_city) parts.push(lead.property_city);
  if (lead.property_state) parts.push(lead.property_state);
  if (lead.property_zip) parts.push(lead.property_zip);
  return parts.filter(Boolean).join(', ');
}

function getDdInfo(lead) {
  if (!lead.dd_deadline) return { formatted: '', daysLeft: null };
  try {
    const deadline = new Date(lead.dd_deadline);
    const now = new Date();
    const daysLeft = Math.ceil((deadline - now) / 86400000);
    const formatted = deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return { formatted, daysLeft };
  } catch { return { formatted: '', daysLeft: null }; }
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [domFilter, setDomFilter] = useState('0');
  const [search, setSearch] = useState('');
  const [sortValue, setSortValue] = useState('created_at:desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [creating, setCreating] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle Gmail OAuth callback redirect
  useEffect(() => {
    if (searchParams.get('gmail') === 'connected') {
      toast.success('Gmail connected successfully! Emails will now be sent from your account.');
      searchParams.delete('gmail');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchLeads = useCallback(async () => {
    try {
      const [sortBy, sortOrder] = sortValue.split(':');
      const params = { sort_by: sortBy, sort_order: sortOrder };
      if (stageFilter !== 'All') params.status = stageFilter;
      if (typeFilter !== 'All') params.property_type = typeFilter;
      if (domFilter !== '0') params.min_dom = domFilter;
      if (search) params.search = search;
      const res = await API.get('/leads', { params });
      setLeads(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [stageFilter, typeFilter, domFilter, search, sortValue]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { window.addEventListener('leads:changed', fetchLeads); return () => window.removeEventListener('leads:changed', fetchLeads); }, [fetchLeads]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.property_address) {
      toast.error('Property address is required');
      return;
    }
    setCreating(true);
    try {
      const payload = { ...form, name: form.property_address };
      if (payload.asking_price) payload.asking_price = parseFloat(payload.asking_price);
      else delete payload.asking_price;
      if (payload.offer_amount) payload.offer_amount = parseFloat(payload.offer_amount);
      else delete payload.offer_amount;
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      payload.name = form.property_address; // ensure name is always set
      const res = await API.post('/leads', payload);
      toast.success('Lead created');
      setDialogOpen(false);
      setForm(defaultForm);
      navigate(`/leads/${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to create lead';
      toast.error(msg);
    } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/leads/${id}`);
      toast.success('Lead deleted');
      fetchLeads();
    } catch { toast.error('Failed to delete'); }
  };

  const handleStageChange = async (leadId, newStatus, prevStatus) => {
    try {
      await API.put(`/leads/${leadId}`, { status: newStatus });
      if (prevStatus) toast.success(`Moved to ${newStatus}`, { action: { label: 'Undo', onClick: () => handleStageChange(leadId, prevStatus) } });
      else toast.success(`Status: ${newStatus}`);
      fetchLeads();
    } catch { toast.error('Failed to update status'); }
  };

  const handleCsvImport = async () => {
    if (!csvFile) { toast.error('Select a CSV file'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await API.post('/leads/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      setCsvDialogOpen(false);
      setCsvFile(null);
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  // Stage counts for filter badges
  const stageCounts = leads.reduce((acc, l) => {
    acc[l.status] = (acc[l.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="leads-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-500 mt-1">{leads.length} lead{leads.length !== 1 ? 's' : ''} in pipeline</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="import-csv-btn" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100">
                <Upload className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Import CSV</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900">
              <DialogHeader>
                <DialogTitle className="text-xl text-gray-900">Import Leads from CSV</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <p className="text-sm text-gray-500">
                  Upload a CSV with columns like: <code className="text-[#0073ea] bg-blue-50 px-1 py-0.5 rounded text-xs">name</code>,{' '}
                  <code className="text-[#0073ea] bg-blue-50 px-1 py-0.5 rounded text-xs">property_address</code>,{' '}
                  <code className="text-[#0073ea] bg-blue-50 px-1 py-0.5 rounded text-xs">offer_amount</code>,{' '}
                  <code className="text-[#0073ea] bg-blue-50 px-1 py-0.5 rounded text-xs">agent_name</code>, etc.
                </p>
                <p className="text-xs text-gray-400">The "name" column is required. All other fields are optional.</p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0073ea]/50 transition-colors bg-[#f6f7fb]">
                  <input type="file" accept=".csv" data-testid="csv-file-input" onChange={e => setCsvFile(e.target.files[0])} className="hidden" id="csv-upload" />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">{csvFile ? csvFile.name : 'Click to select CSV file'}</p>
                  </label>
                </div>
                <Button data-testid="csv-import-submit" onClick={handleCsvImport} disabled={importing || !csvFile} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">
                  {importing ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : `Import ${csvFile ? csvFile.name : 'Leads'}`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-lead-btn" className="bg-[#0073ea] hover:bg-[#0060c2] text-white">
                <Plus className="h-4 w-4 mr-2" /> New Lead
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[85vh] overflow-y-auto" onInteractOutside={(e) => { if (e.target.closest('.pac-container')) e.preventDefault(); }}>
            <DialogHeader>
              <DialogTitle className="text-xl text-gray-900">Create New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Property Address *</Label>
                  <AddressAutocomplete
                    data-testid="lead-address-input"
                    value={form.property_address}
                    onChange={v => setForm(prev => ({ ...prev, property_address: v }))}
                    onPlaceSelect={place => setForm(prev => ({
                      ...prev,
                      property_address: place.street,
                      property_city: place.city,
                      property_state: place.state,
                      property_zip: place.zip,
                    }))}
                    placeholder="Start typing address..."
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">City</Label>
                  <Input value={form.property_city} onChange={e => setForm({ ...form, property_city: e.target.value })} placeholder="City" className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 [&>*:last-child]:max-sm:col-span-2">
                <div className="space-y-2">
                  <Label className="text-gray-700">State</Label>
                  <Input value={form.property_state} onChange={e => setForm({ ...form, property_state: e.target.value })} placeholder="State" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">ZIP</Label>
                  <Input value={form.property_zip} onChange={e => setForm({ ...form, property_zip: e.target.value })} placeholder="ZIP" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Property Type</Label>
                  <Select value={form.property_type} onValueChange={v => setForm({ ...form, property_type: v })}>
                    <SelectTrigger data-testid="property-type-select" className={inputClass}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {['Single Family', 'Multi Family', 'Condo', 'Townhouse', 'Land', 'Commercial'].map(t => (
                        <SelectItem key={t} value={t} className="text-gray-900 hover:bg-gray-50">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Asking Price ($)</Label>
                  <Input type="number" value={form.asking_price} onChange={e => setForm({ ...form, asking_price: e.target.value })} placeholder="0" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Offer Amount ($)</Label>
                  <Input type="number" value={form.offer_amount} onChange={e => setForm({ ...form, offer_amount: e.target.value })} placeholder="0" className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Agent Name</Label>
                  <Input value={form.agent_name} onChange={e => setForm({ ...form, agent_name: e.target.value })} placeholder="Agent name" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Agent Email</Label>
                  <Input type="email" value={form.agent_email} onChange={e => setForm({ ...form, agent_email: e.target.value })} placeholder="agent@email.com" className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Agent Phone</Label>
                  <Input value={form.agent_phone} onChange={e => setForm({ ...form, agent_phone: e.target.value })} placeholder="(555) 123-4567" className={inputClass} />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Listing Broker</Label>
                  <Input value={form.agent_company} onChange={e => setForm({ ...form, agent_company: e.target.value })} placeholder="Broker name" className={inputClass} />
                </div>
              </div>
              <Button type="submit" data-testid="create-lead-submit" disabled={creating} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">
                {creating ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Create Lead'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters & Sort Bar */}
      <div className="space-y-3">
        {/* Search + Sort Row */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="relative w-full sm:w-auto sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="leads-search-input"
              placeholder="Search by name, address, agent, city..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={`pl-9 ${inputClass}`}
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="property-type-filter" className={`flex-1 sm:flex-none sm:w-[160px] ${inputClass}`}>
              <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
              <SelectValue placeholder="Property Type" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {propertyTypes.map(t => (
                <SelectItem key={t} value={t} className="text-gray-900 hover:bg-gray-50">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={domFilter} onValueChange={setDomFilter}>
            <SelectTrigger data-testid="leads-dom-filter" className={`flex-1 sm:flex-none sm:w-[160px] ${inputClass} ${domFilter !== '0' ? '!border-orange-400 !text-orange-300' : ''}`}>
              <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {domOptions.map(([v, l]) => <SelectItem key={v} value={v} className="text-gray-900" data-testid={`leads-dom-option-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortValue} onValueChange={setSortValue}>
            <SelectTrigger data-testid="leads-sort-select" className={`flex-1 sm:flex-none sm:w-[170px] ${inputClass}`}>
              <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {sortOptions.map(o => (
                <SelectItem key={o.value} value={o.value} className="text-gray-900 hover:bg-gray-50">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stage Filter Tabs */}
        <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 no-scrollbar" data-testid="stage-filter-row">
          {stages.map(s => {
            const count = s === 'All' ? leads.length : (stageCounts[s] || 0);
            const isActive = stageFilter === s;
            return (
              <Button
                key={s}
                data-testid={`filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
                variant={isActive ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setStageFilter(s)}
                className={
                  isActive
                    ? 'bg-[#cce5ff] text-[#0073ea] hover:bg-[#b3d9ff] gap-1.5 shrink-0'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100 gap-1.5 shrink-0'
                }
              >
                {s !== 'All' && <span className={`w-2 h-2 rounded-full ${stageDot[s]}`} />}
                {s}
                <span className={`text-[10px] font-mono ml-0.5 ${isActive ? 'text-[#0073ea]/70' : 'text-gray-400'}`}>
                  {count}
                </span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Lead List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-28 bg-gray-200 rounded-xl" />)}
        </div>
      ) : leads.length === 0 ? (
        <Card className="bg-white border-gray-200 border-dashed shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="h-12 w-12 text-gray-300 mb-4" strokeWidth={1.5} />
            <p className="text-gray-500 text-lg font-medium">No leads found</p>
            <p className="text-gray-400 text-sm mt-1">
              {search || stageFilter !== 'All' || typeFilter !== 'All'
                ? 'Try adjusting your filters'
                : 'Create your first lead to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {leads.map(lead => <LeadCard key={lead.id} lead={lead} navigate={navigate} onDelete={handleDelete} onStageChange={handleStageChange} />)}
        </div>
      )}
    </div>
  );
}

/* ─── Lead Card Component ─── */
function LeadCard({ lead, navigate, onDelete, onStageChange }) {
  const fullAddress = buildFullAddress(lead);
  const offerAmt = formatCurrency(lead.offer_amount);
  const askingAmt = formatCurrency(lead.asking_price);
  const badgeClass = stageBadgeClass[lead.status] || '';

  const offerDate = formatDate(lead.offer_sent_date);
  const createdDate = formatDate(lead.created_at);

  const { formatted: ddFormatted, daysLeft } = getDdInfo(lead);

  return (
    <Card
      data-testid={`lead-row-${lead.id}`}
      className="overflow-hidden bg-white border-gray-200 hover:border-[#0073ea]/30 hover:shadow-md transition-[border-color,box-shadow] duration-200 cursor-pointer group"
      onClick={() => navigate(`/leads/${lead.id}`)}
    >
      <MobileLeadCard lead={lead} fullAddress={fullAddress} offerAmt={offerAmt} askingAmt={askingAmt} offerDate={offerDate} badgeClass={badgeClass} onStageChange={onStageChange} onDelete={onDelete} />
      <CardContent className="hidden md:block p-4">
        {/* Row 1: Address, Stage, Offer */}
        <div className="flex items-start gap-3">
          <div className="pt-1 shrink-0">
            <div className={`w-2.5 h-2.5 rounded-full ${stageDot[lead.status] || 'bg-gray-400'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p data-testid={`lead-address-${lead.id}`} className="text-sm font-semibold text-gray-900 truncate">
                {fullAddress || lead.name}
              </p>
              {/* Inline Stage Dropdown */}
              <Select
                value={lead.status}
                onValueChange={(val) => { onStageChange(lead.id, val); }}
              >
                <SelectTrigger
                  data-testid={`lead-stage-select-${lead.id}`}
                  className={`h-6 w-auto min-w-0 px-2 py-0 text-[10px] font-medium border rounded-full gap-1 ${badgeClass}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200" onClick={(e) => e.stopPropagation()}>
                  {editableStages.map(s => (
                    <SelectItem key={s} value={s} className="text-gray-900 hover:bg-gray-50 text-xs">
                      <span className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${stageDot[s]}`} />
                        {s}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Row 2: property type + created date */}
            <div className="flex items-center gap-3 text-xs text-gray-500 mb-1.5">
              {lead.dom > 0 && <span data-testid={`lead-dom-${lead.id}`} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${lead.dom >= 60 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>{lead.dom} DOM</span>}
              {lead.property_type && (
                <span>{lead.property_type}</span>
              )}
            </div>
            {/* Row 3: Agent info */}
            {(lead.agent_name || lead.agent_email || lead.agent_phone) && (
              <div className="flex items-center gap-3 text-xs text-gray-500">
                {lead.agent_name && <span className="font-medium text-gray-600">{lead.agent_name}</span>}
                {lead.agent_phone && (
                  <span data-testid={`lead-agent-phone-${lead.id}`} className="flex items-center gap-1">
                    <Phone className="h-3 w-3 text-gray-400" />
                    {lead.agent_phone}
                  </span>
                )}
                {lead.agent_email && (
                  <span data-testid={`lead-agent-email-${lead.id}`} className="flex items-center gap-1 truncate max-w-[200px]">
                    <Mail className="h-3 w-3 text-gray-400" />
                    {lead.agent_email}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right side: Financial + Date columns */}
          <div className="flex items-start gap-4 shrink-0">
            {/* Offer Amount */}
            <div data-testid={`lead-offer-${lead.id}`} className="text-right min-w-[85px]">
              <p className={`text-sm font-bold ${offerAmt ? 'text-[#00854d]' : 'text-gray-400'}`}>{offerAmt || '--'}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Offer</p>
            </div>

            {/* Counter Amount */}
            {lead.counter_amount > 0 && (
              <div data-testid={`lead-counter-${lead.id}`} className="text-right min-w-[85px]">
                <p className="text-sm font-bold text-orange-600">{formatCurrency(lead.counter_amount)}</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Counter</p>
              </div>
            )}

            {/* Asking Price */}
            <div data-testid={`lead-asking-${lead.id}`} className="text-right min-w-[85px]">
              <p className={`text-sm font-bold ${askingAmt ? 'text-amber-600' : 'text-gray-400'}`}>{askingAmt || '--'}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Asking</p>
            </div>

            {/* ARV */}
            <div data-testid={`lead-arv-${lead.id}`} className="text-right min-w-[85px]">
              <p className={`text-sm font-bold ${lead.arv ? 'text-cyan-600' : 'text-gray-400'}`}>{formatCurrency(lead.arv) || '--'}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">ARV</p>
            </div>

            {/* Date Added */}
            <div data-testid={`lead-created-${lead.id}`} className="text-right min-w-[85px]">
              <p className={`text-xs ${createdDate ? 'text-gray-600' : 'text-gray-400'}`}>{createdDate || '--'}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Added</p>
            </div>

            {/* DD Deadline */}
            <div data-testid={`lead-dd-deadline-${lead.id}`} className="text-right min-w-[85px]">
              {ddFormatted ? (
                <>
                  <p className={`text-xs font-medium ${daysLeft !== null && daysLeft <= 3 ? 'text-red-600' : daysLeft !== null && daysLeft <= 7 ? 'text-amber-600' : 'text-gray-600'}`}>
                    {ddFormatted}
                  </p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                    {daysLeft !== null && daysLeft >= 0 ? `${daysLeft}d left` : daysLeft !== null ? 'Expired' : 'DD Deadline'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-400">--</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">DD Deadline</p>
                </>
              )}
            </div>

            {/* Actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-900 opacity-0 group-hover:opacity-100 transition-opacity">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-gray-200 shadow-lg">
                <DropdownMenuItem data-testid={`view-lead-${lead.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }} className="text-gray-700 hover:bg-gray-50">
                  <Eye className="h-4 w-4 mr-2" /> View Details
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid={`delete-lead-${lead.id}`} onClick={(e) => { e.stopPropagation(); onDelete(lead.id); }} className="text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Phone layout: address, owner + tap-to-call, Offer / ARV / Sent ─── */
const PIPELINE = ['New', 'Offer Sent', 'Follow-Up', 'Under Contract', 'Marketed', 'Sold', 'Closed'];
const nextStage = s => { const i = PIPELINE.indexOf(s); return i >= 0 && i < PIPELINE.length - 1 ? PIPELINE[i + 1] : null; };

function SwipeTray({ lead, phone, onDelete }) {
  const btn = 'flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-semibold text-white';
  return (
    <>
      {phone ? <a href={`tel:${phone}`} className={`${btn} bg-blue-600`} data-testid={`swipe-call-${lead.id}`}><Phone className="h-5 w-5" />Call</a> : <span className={`${btn} bg-gray-600 opacity-50`}><Phone className="h-5 w-5" />Call</span>}
      {phone ? <a href={`sms:${phone}`} className={`${btn} bg-emerald-600`} data-testid={`swipe-text-${lead.id}`}><MessageSquare className="h-5 w-5" />Text</a> : <span className={`${btn} bg-gray-600 opacity-50`}><MessageSquare className="h-5 w-5" />Text</span>}
      <button onClick={() => window.confirm('Delete this lead?') && onDelete(lead.id)} className={`${btn} bg-red-600`} data-testid={`swipe-delete-${lead.id}`}><Trash2 className="h-5 w-5" />Delete</button>
    </>
  );
}

function MobileLeadCard(props) {
  const { lead, onStageChange, onDelete } = props;
  const phone = lead.agent_phone || lead.owner_phone || lead.phone;
  const next = nextStage(lead.status);
  return (
    <div className="md:hidden">
      <SwipeRow testId={`lead-swipe-${lead.id}`} rightLabel={next ? `→ ${next}` : 'Last stage'} onSwipeRight={next ? () => onStageChange(lead.id, next, lead.status) : undefined}
        tray={<SwipeTray lead={lead} phone={phone} onDelete={onDelete} />}>
        <MobileLeadCardBody {...props} />
      </SwipeRow>
    </div>
  );
}

function MobileLeadCardBody({ lead, fullAddress, offerAmt, askingAmt, offerDate, badgeClass, onStageChange }) {
  const arv = formatCurrency(lead.arv);
  const phone = lead.agent_phone || lead.owner_phone || lead.phone;
  const who = lead.agent_name || lead.owner_name || (fullAddress && lead.name !== lead.property_address ? lead.name : '');
  const stat = (label, value, cls, tid) => (
    <div className="min-w-0" data-testid={tid}>
      <p className={`truncate text-sm font-bold tabular-nums ${value ? cls : 'text-gray-400'}`}>{value || '--'}</p>
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
    </div>
  );
  return (
    <div className="p-3.5 space-y-2.5" data-testid={`lead-mobile-card-${lead.id}`}>
      <div className="flex items-start gap-2.5">
        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${stageDot[lead.status] || 'bg-gray-400'}`} />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-snug text-gray-900 line-clamp-2" data-testid={`lead-mobile-address-${lead.id}`}>{fullAddress || lead.name}</p>
          <p className="mt-0.5 truncate text-xs text-gray-500">{[lead.dom > 0 && `${lead.dom} DOM`, lead.property_type, who].filter(Boolean).join(' · ')}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 rounded-lg bg-gray-50 px-3 py-2">
        {offerAmt ? stat('Offer', offerAmt, 'text-[#00854d]', `lead-mobile-offer-${lead.id}`) : stat('Asking', askingAmt, 'text-gray-700', `lead-mobile-offer-${lead.id}`)}
        {stat('ARV', arv, 'text-cyan-600', `lead-mobile-arv-${lead.id}`)}
        {stat('Sent', offerDate, 'text-gray-700', `lead-mobile-sent-${lead.id}`)}
      </div>
      {lead.counter_amount > 0 && <p className="text-xs text-orange-600" data-testid={`lead-mobile-counter-${lead.id}`}>Seller countered at <b>{formatCurrency(lead.counter_amount)}</b></p>}
      <div className="flex items-center justify-between gap-2" onClick={e => e.stopPropagation()}>
        <Select value={lead.status} onValueChange={val => onStageChange(lead.id, val)}>
          <SelectTrigger data-testid={`lead-mobile-stage-${lead.id}`} className={`h-8 w-auto gap-1 rounded-full border px-3 text-xs font-medium ${badgeClass}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            {editableStages.map(s => <SelectItem key={s} value={s} className="text-xs"><span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${stageDot[s]}`} />{s}</span></SelectItem>)}
          </SelectContent>
        </Select>
        {phone ? <a href={`tel:${phone}`} className="flex h-8 items-center gap-1.5 rounded-full border border-gray-200 px-3 text-xs font-medium text-gray-700" data-testid={`lead-mobile-call-${lead.id}`}><Phone className="h-3.5 w-3.5 text-[#0073ea]" />{phone}</a> : <span className="text-[10px] text-gray-500">Swipe ← for actions</span>}
      </div>
    </div>
  );
}
